import React, { Component } from 'react';
import { Drawer, Table, Select, DatePicker, Tag, Button, Progress } from 'antd';
import moment from 'moment';
import { IoCheckmarkDoneCircle } from 'react-icons/io5';
import { GiCancel } from "react-icons/gi";

const { RangePicker } = DatePicker;

const getStatusTag = (status) => {
    const statusColors = {
        COMPLETED: 'green',
        SUCCESS: 'green',
        ERROR: 'red',
        FAILURE: 'red',
        PENDING: 'orange',
        STARTED: 'orange',
        PROGRESS: 'orange',
    };
    return <Tag color={statusColors[status]}>{status}</Tag>;
};

const columns = [
    { title: 'Sl. No', dataIndex: 'key', key: 'key' },
    { title: 'Upload Type', dataIndex: 'uploadType', key: 'uploadType' },
    { title: 'Uploaded By', dataIndex: 'uploadedBy', key: 'uploadedBy' },
    {
        title: 'Upload File',
        dataIndex: 'uploadFile',
        key: 'uploadFile',
        render: (file) => <a href={file.link} target="_blank" rel="noopener noreferrer" style={{ color: '#5B67FF' }}>{file.text}</a>,
    },
    {
        title: 'Output File',
        dataIndex: 'outputFile',
        key: 'outputFile',
        render: (file) => <a href={file.link} target="_blank" rel="noopener noreferrer" style={{ color: '#5B67FF' }}>{file.text}</a>,
    },
    { title: 'Uploaded Date', dataIndex: 'uploadedDate', key: 'uploadedDate' },
    { title: 'ETA', dataIndex: 'eta', key: 'eta' },
    {
        title: 'Progress',
        dataIndex: 'progress',
        key: 'progress',
        render: (val, record) => val != null ? (
            <div>
                {(record.processed_records != null && record.total_records != null) && (
                    <div style={{ fontSize: 12, marginBottom: 2, color: '#555' }}>
                        {record.processed_records}/{record.total_records}
                    </div>
                )}
                <Progress percent={val} size="small" style={{ minWidth: 120 }} />
            </div>
        ) : 'N/A',
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: getStatusTag,
    },
    {
        title: 'Mail Sent',
        dataIndex: 'mail_sent',
        key: 'mail_sent',
        render: (val) => val
            ? <IoCheckmarkDoneCircle size={20} color="green" />
            : <GiCancel size={20} color="red" />,
    },
    { title: 'Remark', dataIndex: 'remark', key: 'error_message' },
];

class UploadHistory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filters: {
                selectedUploadType: null,
                selectedStatus: null,
                selectedDateRange: null,
            },
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
            },
            uploadTypes: [],
            uploadStatuses: [],
        };
    }

    componentDidUpdate(prevProps) {
        const { companyData, uploadHistoryDataCount } = this.props;

        if (prevProps.companyData !== companyData && companyData?.length > 0) {
            const company = companyData[0];
            this.setState({
                uploadTypes: company.file_upload_type || [],
                uploadStatuses: company.file_action_status || [],
            });
        }

        if (prevProps.uploadHistoryDataCount !== uploadHistoryDataCount) {
            this.setState((prevState) => ({
                pagination: {
                    ...prevState.pagination,
                    total: uploadHistoryDataCount,
                },
            }));
        }
    }

    handleClearFilters = () => {
        this.setState({
            filters: {
                selectedUploadType: null,
                selectedStatus: null,
                selectedDateRange: null,
            },
            pagination: {
                ...this.state.pagination,
                current: 1,
            },
        }, () => {
            this.fetchUploadHistory();
        });
    };

    fetchUploadHistory = () => {
        const { filters, pagination } = this.state;
        const { selectedUploadType, selectedStatus, selectedDateRange } = filters;
        const { current, pageSize } = pagination;

        const params = {
            file_upload_type: selectedUploadType,
            upload_status: selectedStatus,
            from_date_search: selectedDateRange ? selectedDateRange[0].startOf('day').valueOf() : null,
            to_date_search: selectedDateRange ? selectedDateRange[1].endOf('day').valueOf() : null,
            limit: pageSize,
            offset: (current - 1) * pageSize,
        };

        this.props.getHistoryUploadList(params);
    };

    handleFilterChange = (newFilter) => {
        this.setState((prevState) => ({
            filters: { ...prevState.filters, ...newFilter },
            pagination: { ...prevState.pagination, current: 1 },
        }), this.fetchUploadHistory);
    };

    handleTableChange = (pagination) => {
        this.setState((prevState) => ({
            pagination: {
                ...prevState.pagination,
                current: pagination.current,
                pageSize: pagination.pageSize,
            },
        }), this.fetchUploadHistory);
    };

    getFilteredData = () => {
        const { uploadHistoryData } = this.props;

        if (!Array.isArray(uploadHistoryData)) return [];

        return uploadHistoryData
            .map((item, index) => ({
                key: index + 1,
                uploadType: item.upload_type_name,
                uploadedBy: item.uploaded_by,
                uploadFile: {
                    text: 'Uploaded File',
                    link: item.input_file,
                },
                outputFile: {
                    text: 'Output File',
                    link: item.output_file,
                },
                uploadedDate: moment(item.created_on).format('DD/MM/YYYY hh:mm A'),
                eta: item.eta ? `${item.eta} (sec)` : 'N/A',
                remark: item.error_message || 'N/A',
                progress: item.progress != null ? item.progress : null,
                processed_records: item.processed_records ?? null,
                total_records: item.total_records ?? null,
                status: item.status ? item.status : item.status_name,
                mail_sent: item.mail_sent,
            }))
    };

    render() {
        const { open, onClose } = this.props;
        const { filters, pagination, uploadTypes, uploadStatuses } = this.state;
        
        return (
            <Drawer
                title="Upload History"
                onClose={onClose}
                open={open}
                zIndex={20000000}
                width="70%"
            >
                <div style={{ display: 'flex', gap: '16px', marginBottom: 20, flexWrap: 'wrap' }}>
                    <Select
                        placeholder="Upload Type"
                        value={filters.selectedUploadType}
                        onChange={(value) => this.handleFilterChange({ selectedUploadType: value })}
                        style={{ width: 200 }}
                        allowClear
                    >
                        {uploadTypes.map((type) => (
                            <Select.Option key={type.id} value={type.id}>
                                {type.name}
                            </Select.Option>
                        ))}
                    </Select>

                    <RangePicker
                        style={{ width: 250 }}
                        value={filters.selectedDateRange}
                        format="DD/MM/YYYY"
                        onChange={(dates) => this.handleFilterChange({ selectedDateRange: dates })}
                    />

                    <Select
                        placeholder="Upload Status"
                        value={filters.selectedStatus}
                        onChange={(value) => this.handleFilterChange({ selectedStatus: value })}
                        style={{ width: 200 }}
                        allowClear
                    >
                        {uploadStatuses.map((status) => (
                            <Select.Option key={status.id} value={status.id}>
                                {status.name}
                            </Select.Option>
                        ))}
                    </Select>

                    <Button onClick={this.handleClearFilters}>Clear Filter</Button>
                    <Button onClick={this.fetchUploadHistory}>Refresh</Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={this.getFilteredData()}
                    loading={this.props.loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={this.handleTableChange}
                    scroll={{ x: true }}
                />
            </Drawer>
        );
    }
}

export default UploadHistory;
